import React, { useMemo, useLayoutEffect, useState, useRef } from 'react';
import {
  WORD_BREAK_STYLE,
  TextItemBackground,
  defaultTextItemBackground,
  BackgroundRenderableItem,
} from './text-item-type';
import { resolveFontFamilyCssStack } from '../../utils/text/font-family-css-stack';
import { turnFontStyleIntoCss } from '../../utils/text/turn-font-style-into-css';

type TextBackgroundRendererProps = {
  /** BackgroundRenderableItem 인터페이스를 만족하는 객체 (TextItem, 캡션 페이지 등) */
  item: BackgroundRenderableItem;
  opacity: number;
  zIndex?: number;
};

/**
 * 텍스트 줄 측정 정보
 */
type LineMeasurement = {
  width: number;
  height: number;
  top: number;
  left: number;
};

/**
 * DOM 기반 텍스트 줄 측정 훅
 * 측정 요소에서 실제 렌더링된 줄의 위치와 너비를 측정합니다.
 */
const useLinesMeasurement = (
  item: BackgroundRenderableItem,
  measureRef: React.RefObject<HTMLDivElement | null>,
): LineMeasurement[] => {
  const [measurements, setMeasurements] = useState<LineMeasurement[]>([]);
  const cssFontFamily = resolveFontFamilyCssStack(item.fontFamily);

  useLayoutEffect(() => {
    const container = measureRef.current;
    if (!container) return;

    const text = item.text;
    if (!text) {
      setMeasurements([]);
      return;
    }

    // 컨테이너 내의 모든 텍스트 노드 찾기
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null,
    );

    const textNodes: Text[] = [];
    let node = walker.nextNode();
    while (node) {
      textNodes.push(node as Text);
      node = walker.nextNode();
    }

    if (textNodes.length === 0) {
      setMeasurements([]);
      return;
    }

    const range = document.createRange();
    const lines: LineMeasurement[] = [];
    const containerRect = container.getBoundingClientRect();

    // 스케일 계산 (실제 렌더링 크기 vs 논리적 크기)
    const scaleX = containerRect.width / item.width;
    const scaleY = containerRect.height / item.height;

    let currentLineTop: number | null = null;
    let currentLineLeft = Infinity;
    let currentLineRight = 0;
    let currentLineHeight = 0;

    // 모든 텍스트 노드의 각 문자를 순회하며 줄 경계 감지
    for (const textNode of textNodes) {
      const nodeText = textNode.textContent || '';

      for (let i = 0; i < nodeText.length; i++) {
        // 줄바꿈 문자는 건너뛰기
        if (nodeText[i] === '\n') {
          if (currentLineTop !== null && currentLineRight > currentLineLeft) {
            // 스케일 보정 적용
            lines.push({
              width: (currentLineRight - currentLineLeft) / scaleX,
              height: currentLineHeight / scaleY,
              top: currentLineTop / scaleY,
              left: currentLineLeft / scaleX,
            });
          }
          currentLineTop = null;
          currentLineLeft = Infinity;
          currentLineRight = 0;
          currentLineHeight = 0;
          continue;
        }

        try {
          range.setStart(textNode, i);
          range.setEnd(textNode, i + 1);
          const rects = range.getClientRects();

          const rect = rects[0];
          if (rect && rect.width > 0) {
            const relativeTop = rect.top - containerRect.top;
            const relativeLeft = rect.left - containerRect.left;
            const relativeRight = rect.right - containerRect.left;

            if (currentLineTop === null) {
              currentLineTop = relativeTop;
              currentLineHeight = rect.height;
            } else if (Math.abs(relativeTop - currentLineTop) > 2) {
              // 이전 줄 저장 (스케일 보정 적용)
              if (currentLineRight > currentLineLeft) {
                lines.push({
                  width: (currentLineRight - currentLineLeft) / scaleX,
                  height: currentLineHeight / scaleY,
                  top: currentLineTop / scaleY,
                  left: currentLineLeft / scaleX,
                });
              }
              currentLineTop = relativeTop;
              currentLineLeft = Infinity;
              currentLineRight = 0;
              currentLineHeight = rect.height;
            }

            currentLineLeft = Math.min(currentLineLeft, relativeLeft);
            currentLineRight = Math.max(currentLineRight, relativeRight);
            currentLineHeight = Math.max(currentLineHeight, rect.height);
          }
        } catch {
          // Range 설정 실패 시 무시
        }
      }
    }

    // 마지막 줄 저장
    if (currentLineTop !== null && currentLineRight > currentLineLeft) {
      lines.push({
        width: (currentLineRight - currentLineLeft) / scaleX,
        height: currentLineHeight / scaleY,
        top: currentLineTop / scaleY,
        left: currentLineLeft / scaleX,
      });
    }

    setMeasurements(lines);
  }, [
    item.text,
    item.width,
    item.height,
    item.fontSize,
    cssFontFamily,
    item.fontStyle,
    item.lineHeight,
    item.letterSpacing,
    item.align,
    item.background?.mode,
    measureRef,
  ]);

  return measurements;
};

/**
 * 텍스트 배경 렌더러 컴포넌트
 * - text-only 모드: 텍스트가 있는 부분만 배경색 적용 (줄별로)
 * - full-area 모드: 텍스트 영역 전체에 배경색 적용 (사각형)
 */
export const TextBackgroundRenderer: React.FC<TextBackgroundRendererProps> = ({
  item,
  opacity,
  zIndex = 0,
}) => {
  const measureRef = useRef<HTMLDivElement>(null);
  const cssFontFamily = resolveFontFamilyCssStack(item.fontFamily);

  const background: TextItemBackground = useMemo(() => {
    return {
      ...defaultTextItemBackground,
      ...(item.background ?? {}),
    };
  }, [item.background]);

  const lineMeasurements = useLinesMeasurement(item, measureRef);

  // 배경이 비활성화되어 있으면 렌더링하지 않음
  if (!background.visible) {
    return null;
  }

  // full-area 모드: 전체 영역에 단일 사각형 배경
  if (background.mode === 'full-area') {
    return (
      <div
        style={{
          position: 'absolute',
          left: item.left - background.horizontalPadding,
          top: item.top - background.verticalPadding,
          width: item.width + background.horizontalPadding * 2,
          height: item.height + background.verticalPadding * 2,
          backgroundColor: background.color,
          opacity: opacity * background.opacity,
          borderRadius: background.borderRadius,
          transform: `rotate(${item.rotation}deg)`,
          transformOrigin: `${background.horizontalPadding}px ${background.verticalPadding}px`,
          pointerEvents: 'none',
          zIndex,
        }}
      />
    );
  }

  // text-only 모드: 줄별로 텍스트 너비에 맞춰 배경
  return (
    <>
      {/* 측정용 요소 - opacity: 0으로 렌더링은 되지만 보이지 않음 */}
      <div
        ref={measureRef}
        style={{
          position: 'absolute',
          left: item.left,
          top: item.top,
          width: item.width,
          height: item.height,
          fontSize: item.fontSize,
          fontFamily: cssFontFamily,
          lineHeight: String(item.lineHeight),
          letterSpacing: `${item.letterSpacing}px`,
          whiteSpace: 'pre-wrap',
          ...WORD_BREAK_STYLE,
          textAlign: item.align,
          opacity: 0,
          pointerEvents: 'none',
          zIndex: -9999,
          ...turnFontStyleIntoCss(item.fontStyle),
        }}
      >
        {item.text}
      </div>

      {/* 배경 렌더링 */}
      <div
        style={{
          position: 'absolute',
          left: item.left,
          top: item.top,
          width: item.width,
          height: item.height,
          transform: `rotate(${item.rotation}deg)`,
          transformOrigin: '0 0',
          pointerEvents: 'none',
          zIndex,
        }}
      >
        {lineMeasurements.map((line, index) => {
          if (line.width === 0) return null;

          const bgWidth = line.width + background.horizontalPadding * 2;
          const bgHeight = line.height + background.verticalPadding * 2;
          // 폰트별 시각적 수직 중앙 보정 (사용자 설정 가능)
          const verticalOffsetPx =
            line.height * (background.verticalOffset / 100);
          const bgTop =
            line.top - background.verticalPadding - verticalOffsetPx;
          const bgLeft = line.left - background.horizontalPadding;

          return (
            <div
              key={`bg-line-${index}`}
              style={{
                position: 'absolute',
                left: bgLeft,
                top: bgTop,
                width: bgWidth,
                height: bgHeight,
                backgroundColor: background.color,
                opacity: opacity * background.opacity,
                borderRadius: background.borderRadius,
                pointerEvents: 'none',
              }}
            />
          );
        })}
      </div>
    </>
  );
};
